/**
 * Modal Dialog Component
 *
 * A reusable modal dialog component with:
 * - Overlay/backdrop with glass morphism effect
 * - Open/close animations
 * - Close on escape key
 * - Close on backdrop click
 * - Proper focus management (focus trap)
 * - Confirmation dialog variant
 */

import { renderIcon, type IconName } from '../utils/icons';

/**
 * Modal size variants
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * Modal variant for different use cases
 */
export type ModalVariant = 'default' | 'confirm' | 'alert' | 'form';

/**
 * Button variant for modal actions
 */
export type ButtonVariant = 'primary' | 'secondary' | 'destructive' | 'ghost';

/**
 * Modal action button configuration
 */
export interface ModalAction {
  /** Button label */
  label: string;
  /** Button variant */
  variant?: ButtonVariant;
  /** Click handler - return false to prevent modal close */
  onClick?: () => boolean | void | Promise<boolean | void>;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Whether this is the primary action (auto-focused) */
  primary?: boolean;
  /** Optional icon */
  icon?: IconName;
  /** Loading state */
  loading?: boolean;
}

/**
 * Modal configuration options
 */
export interface ModalOptions {
  /** Modal title */
  title?: string;
  /** Modal content (string or HTMLElement) */
  content?: string | HTMLElement;
  /** Modal size variant */
  size?: ModalSize;
  /** Modal variant */
  variant?: ModalVariant;
  /** Whether to close on backdrop click (default: true) */
  closeOnBackdropClick?: boolean;
  /** Whether to close on escape key (default: true) */
  closeOnEscape?: boolean;
  /** Whether to show close button (default: true) */
  showCloseButton?: boolean;
  /** Custom CSS class for the modal container */
  className?: string;
  /** Action buttons */
  actions?: ModalAction[];
  /** Callback when modal opens */
  onOpen?: () => void;
  /** Callback when modal closes */
  onClose?: () => void;
  /** Callback before close - return false to prevent close */
  onBeforeClose?: () => boolean | Promise<boolean>;
  /** Optional icon for the header */
  icon?: IconName;
  /** Optional icon color class */
  iconClass?: string;
}

/**
 * Size mappings for modal widths
 */
const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-4xl',
};

/**
 * Button variant classes
 */
const BUTTON_VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-foreground hover:bg-accent-600 focus:ring-primary',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-neutral-700 focus:ring-secondary',
  destructive: 'bg-destructive text-destructive-foreground hover:opacity-90 focus:ring-destructive',
  ghost: 'bg-transparent text-foreground hover:bg-neutral-800 focus:ring-neutral-500',
};

/**
 * Get all focusable elements within a container
 */
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors));
}

/**
 * Modal Dialog class
 */
export class Modal {
  private overlay: HTMLDivElement | null = null;
  private container: HTMLDivElement | null = null;
  private options: Required<ModalOptions>;
  private previousActiveElement: HTMLElement | null = null;
  private isOpen = false;
  private boundKeyHandler: (e: KeyboardEvent) => void;

  constructor(options: ModalOptions = {}) {
    this.options = {
      title: options.title ?? '',
      content: options.content ?? '',
      size: options.size ?? 'md',
      variant: options.variant ?? 'default',
      closeOnBackdropClick: options.closeOnBackdropClick ?? true,
      closeOnEscape: options.closeOnEscape ?? true,
      showCloseButton: options.showCloseButton ?? true,
      className: options.className ?? '',
      actions: options.actions ?? [],
      onOpen: options.onOpen ?? (() => {}),
      onClose: options.onClose ?? (() => {}),
      onBeforeClose: options.onBeforeClose ?? (() => true),
      icon: options.icon ?? (null as unknown as IconName),
      iconClass: options.iconClass ?? '',
    };

    this.boundKeyHandler = this.handleKeyDown.bind(this);
  }

  /**
   * Open the modal
   */
  open(): void {
    if (this.isOpen) return;

    // Store currently focused element
    this.previousActiveElement = document.activeElement as HTMLElement;

    // Create and append modal elements
    this.createModal();

    // Add keyboard listener
    document.addEventListener('keydown', this.boundKeyHandler);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Trigger open callback
    this.options.onOpen();

    // Animate in
    requestAnimationFrame(() => {
      if (this.overlay) {
        this.overlay.classList.remove('opacity-0');
        this.overlay.classList.add('opacity-100');
      }
      if (this.container) {
        this.container.classList.remove('scale-95', 'opacity-0');
        this.container.classList.add('scale-100', 'opacity-100');
      }

      // Focus first focusable element or primary action
      this.setInitialFocus();
    });

    this.isOpen = true;
  }

  /**
   * Close the modal
   */
  async close(): Promise<void> {
    if (!this.isOpen) return;

    // Check if close is allowed
    const canClose = await this.options.onBeforeClose();
    if (canClose === false) return;

    // Animate out
    if (this.overlay) {
      this.overlay.classList.remove('opacity-100');
      this.overlay.classList.add('opacity-0');
    }
    if (this.container) {
      this.container.classList.remove('scale-100', 'opacity-100');
      this.container.classList.add('scale-95', 'opacity-0');
    }

    // Wait for animation to complete
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Remove from DOM
    this.destroy();

    // Restore body scroll
    document.body.style.overflow = '';

    // Restore focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }

    // Trigger close callback
    this.options.onClose();

    this.isOpen = false;
  }

  /**
   * Update modal content
   */
  setContent(content: string | HTMLElement): void {
    const contentContainer = this.container?.querySelector('.modal-content');
    if (!contentContainer) return;

    contentContainer.innerHTML = '';
    if (typeof content === 'string') {
      contentContainer.innerHTML = content;
    } else {
      contentContainer.appendChild(content);
    }
  }

  /**
   * Update modal title
   */
  setTitle(title: string): void {
    const titleElement = this.container?.querySelector('.modal-title');
    if (titleElement) {
      titleElement.textContent = title;
    }
  }

  /**
   * Check if modal is currently open
   */
  getIsOpen(): boolean {
    return this.isOpen;
  }

  /**
   * Destroy the modal and clean up
   */
  private destroy(): void {
    // Remove keyboard listener
    document.removeEventListener('keydown', this.boundKeyHandler);

    // Remove from DOM
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }

    this.container = null;
  }

  /**
   * Create the modal DOM elements
   */
  private createModal(): void {
    // Create overlay
    this.overlay = document.createElement('div');
    this.overlay.className = `
      fixed inset-0 z-50 flex items-center justify-center p-4
      bg-neutral-950/80 backdrop-blur-sm
      opacity-0 transition-opacity duration-200
    `.replace(/\s+/g, ' ').trim();

    // Handle backdrop click
    if (this.options.closeOnBackdropClick) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.close();
        }
      });
    }

    // Create container
    this.container = document.createElement('div');
    this.container.className = `
      relative w-full ${SIZE_CLASSES[this.options.size]}
      glass-card rounded-xl
      scale-95 opacity-0 transition-all duration-200
      ${this.options.className}
    `.replace(/\s+/g, ' ').trim();
    this.container.setAttribute('role', 'dialog');
    this.container.setAttribute('aria-modal', 'true');
    if (this.options.title) {
      this.container.setAttribute('aria-labelledby', 'modal-title');
    }

    // Build modal content
    this.container.innerHTML = this.buildModalContent();

    // Add event listeners to action buttons
    this.attachActionListeners();

    // Append to DOM
    this.overlay.appendChild(this.container);
    document.body.appendChild(this.overlay);
  }

  /**
   * Build the modal HTML content
   */
  private buildModalContent(): string {
    const { title, content, showCloseButton, actions, icon, iconClass } = this.options;

    let html = '';

    // Header
    if (title || showCloseButton) {
      html += `
        <div class="flex items-center justify-between p-4 border-b border-neutral-800">
          <div class="flex items-center gap-3">
            ${icon ? `<span class="modal-icon ${iconClass}">${renderIcon(icon, { size: 'md' }).outerHTML}</span>` : ''}
            <h2 id="modal-title" class="modal-title text-lg font-semibold text-foreground">${title}</h2>
          </div>
          ${showCloseButton ? `
            <button
              type="button"
              class="modal-close-btn p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-neutral-800 transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Close modal"
            >
              ${renderIcon('x', { size: 'sm' }).outerHTML}
            </button>
          ` : ''}
        </div>
      `;
    }

    // Content
    html += `
      <div class="modal-content p-4 text-foreground">
        ${typeof content === 'string' ? content : ''}
      </div>
    `;

    // Actions
    if (actions.length > 0) {
      html += `
        <div class="flex items-center justify-end gap-2 p-4 border-t border-neutral-800">
          ${actions.map((action, index) => this.buildActionButton(action, index)).join('')}
        </div>
      `;
    }

    return html;
  }

  /**
   * Build an action button HTML
   */
  private buildActionButton(action: ModalAction, index: number): string {
    const variant = action.variant ?? 'secondary';
    const variantClasses = BUTTON_VARIANT_CLASSES[variant];

    return `
      <button
        type="button"
        class="modal-action-btn inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-neutral-900 ${variantClasses}"
        data-action-index="${index}"
        ${action.disabled ? 'disabled' : ''}
        ${action.primary ? 'data-primary="true"' : ''}
      >
        ${action.loading ? renderIcon('loader', { size: 'sm', className: 'animate-spin' }).outerHTML : ''}
        ${action.icon && !action.loading ? renderIcon(action.icon, { size: 'sm' }).outerHTML : ''}
        ${action.label}
      </button>
    `;
  }

  /**
   * Attach event listeners to action buttons
   */
  private attachActionListeners(): void {
    if (!this.container) return;

    // Close button
    const closeBtn = this.container.querySelector('.modal-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Action buttons
    const actionBtns = this.container.querySelectorAll('.modal-action-btn');
    actionBtns.forEach((btn) => {
      const index = parseInt(btn.getAttribute('data-action-index') ?? '0', 10);
      const action = this.options.actions[index];

      if (action?.onClick) {
        btn.addEventListener('click', async () => {
          const result = await action.onClick!();
          // Close modal unless handler returns false
          if (result !== false) {
            this.close();
          }
        });
      } else {
        // Default: close modal
        btn.addEventListener('click', () => this.close());
      }
    });

    // If content is an HTMLElement, append it now
    if (typeof this.options.content !== 'string') {
      const contentContainer = this.container.querySelector('.modal-content');
      if (contentContainer && this.options.content) {
        contentContainer.appendChild(this.options.content);
      }
    }
  }

  /**
   * Handle keyboard events (escape key, tab for focus trap)
   */
  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.container) return;

    // Close on escape
    if (e.key === 'Escape' && this.options.closeOnEscape) {
      e.preventDefault();
      this.close();
      return;
    }

    // Focus trap on tab
    if (e.key === 'Tab') {
      const focusableElements = getFocusableElements(this.container);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!firstElement || !lastElement) return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }

  /**
   * Set initial focus when modal opens
   */
  private setInitialFocus(): void {
    if (!this.container) return;

    // Try to focus primary action button first
    const primaryBtn = this.container.querySelector<HTMLButtonElement>('[data-primary="true"]');
    if (primaryBtn) {
      primaryBtn.focus();
      return;
    }

    // Otherwise focus first focusable element
    const focusableElements = getFocusableElements(this.container);
    const firstElement = focusableElements[0];
    if (firstElement) {
      firstElement.focus();
    }
  }
}

/**
 * Create and open a modal with the given options
 */
export function openModal(options: ModalOptions): Modal {
  const modal = new Modal(options);
  modal.open();
  return modal;
}

/**
 * Confirmation dialog options
 */
export interface ConfirmOptions {
  /** Dialog title */
  title: string;
  /** Dialog message */
  message: string;
  /** Confirm button text (default: "Confirm") */
  confirmText?: string;
  /** Cancel button text (default: "Cancel") */
  cancelText?: string;
  /** Whether this is a destructive action */
  destructive?: boolean;
  /** Optional icon */
  icon?: IconName;
  /** Optional icon color class */
  iconClass?: string;
}

/**
 * Show a confirmation dialog
 * Returns a promise that resolves to true if confirmed, false if cancelled
 */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const {
      title,
      message,
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      destructive = false,
      icon,
      iconClass,
    } = options;

    const modal = new Modal({
      title,
      content: `<p class="text-muted-foreground">${message}</p>`,
      variant: 'confirm',
      size: 'sm',
      icon: icon ?? (destructive ? 'alertTriangle' : 'info'),
      iconClass: iconClass ?? (destructive ? 'text-destructive' : 'text-info'),
      showCloseButton: false,
      closeOnBackdropClick: false,
      actions: [
        {
          label: cancelText,
          variant: 'ghost',
          onClick: () => {
            resolve(false);
          },
        },
        {
          label: confirmText,
          variant: destructive ? 'destructive' : 'primary',
          primary: true,
          onClick: () => {
            resolve(true);
          },
        },
      ],
      onClose: () => {
        // If closed without action, treat as cancel
        resolve(false);
      },
    });

    modal.open();
  });
}

/**
 * Alert dialog options
 */
export interface AlertOptions {
  /** Dialog title */
  title: string;
  /** Dialog message */
  message: string;
  /** Button text (default: "OK") */
  buttonText?: string;
  /** Alert type */
  type?: 'info' | 'success' | 'warning' | 'error';
}

/**
 * Show an alert dialog
 * Returns a promise that resolves when acknowledged
 */
export function alert(options: AlertOptions): Promise<void> {
  return new Promise((resolve) => {
    const {
      title,
      message,
      buttonText = 'OK',
      type = 'info',
    } = options;

    type AlertType = 'info' | 'success' | 'warning' | 'error';

    const iconMap: Record<AlertType, IconName> = {
      info: 'info',
      success: 'checkCircle',
      warning: 'alertTriangle',
      error: 'xCircle',
    };

    const iconClassMap: Record<AlertType, string> = {
      info: 'text-info',
      success: 'text-success',
      warning: 'text-warning',
      error: 'text-destructive',
    };

    const modal = new Modal({
      title,
      content: `<p class="text-muted-foreground">${message}</p>`,
      variant: 'alert',
      size: 'sm',
      icon: iconMap[type],
      iconClass: iconClassMap[type],
      showCloseButton: false,
      closeOnBackdropClick: false,
      closeOnEscape: false,
      actions: [
        {
          label: buttonText,
          variant: 'primary',
          primary: true,
          onClick: () => {
            resolve();
          },
        },
      ],
    });

    modal.open();
  });
}

/**
 * Prompt dialog options
 */
export interface PromptOptions {
  /** Dialog title */
  title: string;
  /** Dialog message */
  message?: string;
  /** Default value */
  defaultValue?: string;
  /** Input placeholder */
  placeholder?: string;
  /** Confirm button text (default: "OK") */
  confirmText?: string;
  /** Cancel button text (default: "Cancel") */
  cancelText?: string;
  /** Input validation function */
  validate?: (value: string) => string | null;
}

/**
 * Show a prompt dialog with an input field
 * Returns a promise that resolves to the input value if confirmed, null if cancelled
 */
export function prompt(options: PromptOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const {
      title,
      message,
      defaultValue = '',
      placeholder = '',
      confirmText = 'OK',
      cancelText = 'Cancel',
      validate,
    } = options;

    // Create input element
    const contentDiv = document.createElement('div');
    contentDiv.className = 'space-y-4';

    if (message) {
      const msgP = document.createElement('p');
      msgP.className = 'text-muted-foreground';
      msgP.textContent = message;
      contentDiv.appendChild(msgP);
    }

    const inputWrapper = document.createElement('div');
    inputWrapper.className = 'space-y-1';

    const input = document.createElement('input');
    input.type = 'text';
    input.value = defaultValue;
    input.placeholder = placeholder;
    input.className = `
      w-full px-3 py-2 rounded-md
      glass-input text-foreground placeholder:text-muted-foreground
      focus:outline-none
    `.replace(/\s+/g, ' ').trim();

    const errorDiv = document.createElement('div');
    errorDiv.className = 'text-sm text-destructive hidden';

    inputWrapper.appendChild(input);
    inputWrapper.appendChild(errorDiv);
    contentDiv.appendChild(inputWrapper);

    let modalInstance: Modal;

    const handleConfirm = (): boolean => {
      const value = input.value;

      if (validate) {
        const error = validate(value);
        if (error) {
          errorDiv.textContent = error;
          errorDiv.classList.remove('hidden');
          input.focus();
          return false;
        }
      }

      resolve(value);
      return true;
    };

    modalInstance = new Modal({
      title,
      content: contentDiv,
      variant: 'form',
      size: 'sm',
      showCloseButton: true,
      actions: [
        {
          label: cancelText,
          variant: 'ghost',
          onClick: () => {
            resolve(null);
          },
        },
        {
          label: confirmText,
          variant: 'primary',
          primary: true,
          onClick: handleConfirm,
        },
      ],
      onClose: () => {
        resolve(null);
      },
      onOpen: () => {
        // Focus input after modal opens
        setTimeout(() => {
          input.focus();
          input.select();
        }, 50);
      },
    });

    // Handle enter key in input
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (handleConfirm()) {
          modalInstance.close();
        }
      }
    });

    // Clear error on input
    input.addEventListener('input', () => {
      errorDiv.classList.add('hidden');
    });

    modalInstance.open();
  });
}
